import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Tv, Calendar, Clock, Globe, ExternalLink, Youtube, Radio, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";

// YouTube channels for West Ham content
const YOUTUBE_CHANNELS = [
  {
    name: "West Ham United Official",
    url: "https://www.youtube.com/c/westhamunited",
    description: "Official club channel - Match highlights, interviews, behind the scenes, U21 & U18 matches",
    subscribers: "767K",
    isOfficial: true
  },
  {
    name: "West Ham Fan TV",
    url: "https://www.youtube.com/c/WESTHAMFANTV",
    description: "Fan reactions, match previews, post-match analysis, live watch-alongs",
    subscribers: "85K",
    isOfficial: false
  },
  {
    name: "The West Ham Way",
    url: "https://www.youtube.com/c/TheWestHamWay",
    description: "Daily content, watch alongs, fan discussions, transfer news with ExWHUemployee",
    subscribers: "16K",
    isOfficial: false
  },
  {
    name: "Hammers Chat",
    url: "https://www.youtube.com/c/HammersChat",
    description: "Live discussion shows, match analysis, transfer updates",
    subscribers: "35K",
    isOfficial: false
  },
  {
    name: "Hammers Chat Forum",
    url: "https://www.youtube.com/c/HammersChatForum",
    description: "Community hub, extended discussions, fan podcasts",
    subscribers: "10K",
    isOfficial: false
  }
];

export default function TVPage() {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState("uk");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const regions = [
    { id: "uk", label: "🇬🇧 UK & Ireland", flag: "🇬🇧" },
    { id: "usa", label: "🇺🇸 USA", flag: "🇺🇸" },
    { id: "canada", label: "🇨🇦 Canada", flag: "🇨🇦" },
    { id: "australia", label: "🇦🇺 Australia", flag: "🇦🇺" },
    { id: "europe", label: "🇪🇺 Europe", flag: "🇪🇺" },
    { id: "asia", label: "🌏 Asia", flag: "🌏" },
    { id: "africa", label: "🌍 Africa", flag: "🌍" },
    { id: "middleEast", label: "🇦🇪 Middle East", flag: "🇦🇪" },
    { id: "latinAmerica", label: "🌎 Latin America", flag: "🌎" },
    { id: "worldwide", label: "🌐 Worldwide", flag: "🌐" },
  ];

  useEffect(() => {
    fetchTVFixtures();
  }, []);

  const fetchTVFixtures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tv/fixtures`);
      setFixtures(response.data.fixtures || []);
      setLastUpdated(response.data.last_updated);
      setDateRange(response.data.date_range);
    } catch (error) {
      console.error("Failed to fetch TV fixtures:", error);
      // Fall back to regular fixtures if TV API fails
      try {
        const fallbackResponse = await axios.get(`${API}/fixtures`);
        const allFixtures = fallbackResponse.data;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const twoWeeksAhead = new Date(today);
        twoWeeksAhead.setDate(twoWeeksAhead.getDate() + 14);
        
        const upcoming = allFixtures
          .filter(fixture => {
            const fixtureDate = new Date(fixture.date);
            return fixtureDate >= today && fixtureDate <= twoWeeksAhead && !fixture.is_completed;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        setFixtures(upcoming);
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const getChannelsForRegion = (fixture) => {
    // First check if we have regional channels from the scraper / fallback table
    if (fixture.regional_channels && fixture.regional_channels[selectedRegion]?.length > 0) {
      return fixture.regional_channels[selectedRegion];
    }

    // For the UK we also accept the legacy tv_channels array (scraper writes
    // to that field directly). For other regions we deliberately DO NOT fall
    // back to UK channels — showing the UK broadcaster to a viewer in the
    // USA / Australia / Asia etc. is misleading.
    if (selectedRegion === "uk" && fixture.tv_channels && fixture.tv_channels.length > 0) {
      return fixture.tv_channels;
    }

    // No confirmed broadcast in this region — let the UI render "TBA".
    return [];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const getCompetitionBadge = (competition, opponent = "", team = "") => {
    const comp = (competition || "").toLowerCase();
    const opp = (opponent || "").toLowerCase();
    const tm = (team || "").toLowerCase();
    
    // Check opponent name for U18/U21 indicators
    if (opp.includes("u18") || opp.includes("under 18") || tm.includes("u18")) {
      return { bg: "bg-[#2BAEE5]", text: "text-white", label: "U18s" };
    }
    if (opp.includes("u21") || opp.includes("under 21") || tm.includes("u21")) {
      return { bg: "bg-[#7C2C3B]", text: "text-white", label: "U21s" };
    }
    
    // Check competition name for academy competitions
    if (comp.includes("u21") || comp.includes("under 21") || comp.includes("premier league 2") || comp.includes("pl2")) {
      return { bg: "bg-[#7C2C3B]", text: "text-white", label: "U21s" };
    }
    if (comp.includes("u18") || comp.includes("under 18")) {
      return { bg: "bg-[#2BAEE5]", text: "text-white", label: "U18s" };
    }
    if (comp.includes("wsl") || comp.includes("women")) {
      return { bg: "bg-purple-600", text: "text-white", label: "Women's" };
    }
    
    // First team competitions
    if (comp.includes("premier league") || comp === "premier") {
      return { bg: "bg-[#3D195B]", text: "text-white", label: "Premier League" };
    }
    if (comp.includes("fa cup")) {
      return { bg: "bg-[#E90052]", text: "text-white", label: "FA Cup" };
    }
    if (comp.includes("efl") || comp.includes("carabao") || comp.includes("league cup")) {
      return { bg: "bg-[#00A650]", text: "text-white", label: "EFL Cup" };
    }
    if (comp.includes("conference") || comp.includes("europa")) {
      return { bg: "bg-[#097F4A]", text: "text-white", label: "Conference League" };
    }
    
    return { bg: "bg-slate-500", text: "text-white", label: competition || "Match" };
  };

  const getTeamBadge = (team) => {
    if (team === "Under 21s" || team === "U21") {
      return { bg: "bg-[#7C2C3B]", label: "U21" };
    } else if (team === "Under 18s" || team === "U18") {
      return { bg: "bg-[#2BAEE5]", label: "U18" };
    } else if (team === "Women") {
      return { bg: "bg-purple-600", label: "Women" };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="TV Guide" path="/tv" description="West Ham United TV schedule. Find out which channel is showing the next Hammers match with regional broadcast information." />
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#7C2C3B] to-[#0F172A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
              <Tv className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                ALL WHUFC GAMES TV
              </h1>
              <p className="text-white/70">All upcoming matches and confirmed broadcasters</p>
            </div>
          </div>
          
          {/* Auto-update notice */}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Next 14 days • Updates automatically</span>
            </div>
            {lastUpdated && (
              <div className="text-white/50 text-sm">
                Last updated: {new Date(lastUpdated).toLocaleString('en-GB')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Region Selector */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-[#7C2C3B]" />
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              SELECT YOUR REGION
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {regions.map((region) => (
              <button
                key={region.id}
                onClick={() => setSelectedRegion(region.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedRegion === region.id
                    ? "bg-[#7C2C3B] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {region.label}
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming Fixtures */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-[#7C2C3B]" />
              <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                UPCOMING MATCHES
              </h2>
            </div>
            <Button 
              onClick={fetchTVFixtures} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-[#7C2C3B] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : fixtures.length === 0 ? (
            <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
              <Tv className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-600 mb-2">No Matches Scheduled</h3>
              <p className="text-slate-500">No West Ham matches scheduled in the next 14 days. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {fixtures.map((fixture, index) => {
                const badge = getCompetitionBadge(fixture.competition, fixture.opponent, fixture.team);
                const teamBadge = getTeamBadge(fixture.team);
                const channels = getChannelsForRegion(fixture);
                const hasConfirmedBroadcast = channels.length > 0;
                
                return (
                  <div 
                    key={fixture.id || index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Match Header */}
                    <div className="bg-gradient-to-r from-slate-50 to-white px-4 md:px-6 py-3 md:py-4 border-b border-slate-100">
                      <div className="flex flex-col gap-2 md:gap-4">
                        {/* Badges and Date/Time */}
                        <div className="flex flex-wrap items-center gap-2">
                          <div className={`px-2 md:px-3 py-1 rounded-full text-xs font-bold ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </div>
                          {teamBadge && (
                            <div className={`px-2 md:px-3 py-1 rounded-full text-xs font-bold ${teamBadge.bg} text-white`}>
                              {teamBadge.label}
                            </div>
                          )}
                        </div>
                        {/* Date, Time, Venue - Stack on mobile */}
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-4">
                          <div className="flex flex-wrap items-center gap-2 md:gap-3 text-slate-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="text-xs md:text-sm font-medium">{formatDate(fixture.date)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="text-xs md:text-sm font-medium">{fixture.time || "TBC"} GMT</span>
                            </div>
                          </div>
                          <div className="text-xs md:text-sm text-slate-500">
                            {fixture.home_away === "Home" ? "Home" : "Away"} • {fixture.venue || (fixture.home_away === "Home" ? "London Stadium" : "Away")}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Match Details */}
                    <div className="px-4 md:px-6 py-4 md:py-6">
                      {/* Mobile Layout - Stacked */}
                      <div className="flex flex-col md:hidden gap-3 mb-4">
                        {/* Home Team */}
                        <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                          <div className="w-10 h-10 rounded-full bg-[#7C2C3B] flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-xs">
                              {fixture.home_away === "Home" ? "WHU" : fixture.opponent?.substring(0, 3).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-base font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                            {fixture.home_away === "Home" ? "WEST HAM UNITED" : fixture.opponent?.toUpperCase()}
                          </span>
                          {fixture.home_away === "Home" && (
                            <span className="ml-auto text-xs bg-[#7C2C3B] text-white px-2 py-0.5 rounded">HOME</span>
                          )}
                        </div>
                        
                        {/* VS Divider */}
                        <div className="text-center text-lg font-bold text-slate-300">VS</div>
                        
                        {/* Away Team */}
                        <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                          <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0">
                            <span className="text-slate-700 font-bold text-xs">
                              {fixture.home_away === "Away" ? "WHU" : fixture.opponent?.substring(0, 3).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-base font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                            {fixture.home_away === "Away" ? "WEST HAM UNITED" : fixture.opponent?.toUpperCase()}
                          </span>
                          {fixture.home_away === "Away" && (
                            <span className="ml-auto text-xs bg-slate-600 text-white px-2 py-0.5 rounded">AWAY</span>
                          )}
                        </div>
                      </div>

                      {/* Desktop Layout - Horizontal */}
                      <div className="hidden md:flex items-center justify-center gap-6 mb-6">
                        <div className="flex-1 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                              {fixture.home_away === "Home" ? "WEST HAM UNITED" : fixture.opponent?.toUpperCase()}
                            </span>
                            <div className="w-12 h-12 rounded-full bg-[#7C2C3B] flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-sm">
                                {fixture.home_away === "Home" ? "WHU" : fixture.opponent?.substring(0, 3).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-3xl font-bold text-slate-300 flex-shrink-0">VS</div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-slate-600 font-bold text-sm">
                                {fixture.home_away === "Away" ? "WHU" : fixture.opponent?.substring(0, 3).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                              {fixture.home_away === "Away" ? "WEST HAM UNITED" : fixture.opponent?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* TV Channels */}
                      <div className={`rounded-lg p-4 ${hasConfirmedBroadcast ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          {hasConfirmedBroadcast ? (
                            <>
                              <Tv className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-bold text-green-700">
                                CONFIRMED BROADCASTERS ({regions.find(r => r.id === selectedRegion)?.flag})
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-bold text-amber-700">
                                BROADCAST TO BE CONFIRMED
                              </span>
                            </>
                          )}
                        </div>
                        
                        {hasConfirmedBroadcast ? (
                          <div className="flex flex-wrap gap-2">
                            {channels.map((channel, idx) => (
                              <span 
                                key={idx}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-300 rounded-full text-sm font-medium text-slate-700"
                              >
                                <Radio className="w-3 h-3 text-green-600" />
                                {channel}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-amber-700">
                            {fixture.team === "Under 21s" || fixture.team === "U21" || fixture.team === "Under 18s" || fixture.team === "U18" 
                              ? "Academy matches are typically shown on WHUFC YouTube"
                              : "Broadcaster details will be added when confirmed. Check back closer to kick-off."
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* YouTube Channels Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Youtube className="w-6 h-6 text-red-600" />
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              YOUTUBE CHANNELS
            </h2>
          </div>
          
          <p className="text-slate-600 mb-4">
            U21 and U18 matches are broadcast live on the official West Ham YouTube channel. 
            Fan channels provide watch alongs and analysis for all matches.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {YOUTUBE_CHANNELS.map((channel, index) => (
              <a
                key={index}
                href={channel.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-red-300 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    channel.isOfficial ? "bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b]" : "bg-red-600"
                  }`}>
                    <Youtube className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-800 truncate">{channel.name}</h3>
                      {channel.isOfficial && (
                        <span className="px-2 py-0.5 bg-[#7C2C3B] text-white text-xs rounded-full font-medium flex-shrink-0">
                          Official
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{channel.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-slate-400">{channel.subscribers} subscribers</span>
                      <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-red-500 transition-colors" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* All Sport Channel Link */}
        <div className="bg-gradient-to-r from-[#0F172A] to-[#1e293b] rounded-xl p-6 border border-slate-700">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F8D742] to-[#e6c23a] flex items-center justify-center">
                <Globe className="w-8 h-8 text-[#0F172A]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                  ALL SPORT CHANNEL
                </h3>
                <p className="text-slate-400">Watch live sports from around the world</p>
              </div>
            </div>
            <a
              href="http://www.fawanews.sc/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#F8D742] hover:bg-yellow-300 text-[#0F172A] rounded-xl font-bold transition-colors"
            >
              <Tv className="w-5 h-5" />
              VISIT FAWA NEWS
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>
            TV listings are scraped from official sources and updated regularly.
            <br />
            All times shown in GMT. Please verify with your local broadcaster before kick-off.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
